import React, { useEffect, useState } from 'react';
import {
  useForm,
  Controller,
  FormProvider,
  type FieldErrors,
  type Resolver,
  type UseFormReturn,
  type ControllerRenderProps,
} from 'react-hook-form';
import { z } from 'zod/v4';
import { zodResolver } from '@hookform/resolvers/zod';
import { Switch } from '../core/switch';
import { MultipleSelector, Option } from '../core/multiple-selector';
import { DatePicker } from '../core/datepicker';
import { DateTimeField } from './date-time-field';
import { TextArea } from '../core/textarea';
import { MediaUploader } from './media-uploader';
import { Input } from '../core/input';
import { Label } from '../core/label';
import { Button } from '../core/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../core/select';
import { useFormDateSerializer } from '../../hooks/useFormDateSerializer';
import { cn } from '../../utils';
import { useTranslation } from 'react-i18next';

interface DynamicFormProps<T extends z.ZodTypeAny> {
  schema: T;
  onSubmit: (data: z.infer<T>) => Promise<void>;
  onClose?: () => void;
  defaultValues?: Partial<z.infer<T>>;
  isEditing?: boolean;
  foreignKeyOptions?: Record<string, { id: number; label: string }[] | Option[]>;
  labelMappings?: Record<string, string>;
  hiddenFields?: string[];
  hideButtons?: boolean;
  formRef?: React.RefObject<HTMLFormElement>;
  customRenderers?: Record<string, (context: {
    field: ControllerRenderProps<z.infer<T>>;
    error?: string;
    methods: UseFormReturn<z.infer<T>>;
  }) => React.ReactNode>;
  fieldVisibility?: (context: { key: string; values: z.infer<T>; isEditing: boolean }) => boolean;
  /**
   * Called with the current (unvalidated) values whenever any field changes,
   * and once after the defaults are applied — for live previews beside the form.
   */
  onValuesChange?: (values: Partial<z.infer<T>>) => void;
  /** Called whenever the form becomes dirty / clean again (edits vs. the defaults). */
  onDirtyChange?: (dirty: boolean) => void;
}

export default function DynamicForm<T extends z.ZodTypeAny>({
  schema,
  onSubmit,
  onClose,
  defaultValues = {},
  isEditing = false,
  foreignKeyOptions = {},
  labelMappings = {},
  hiddenFields = [],
  hideButtons = false,
  formRef,
  customRenderers = {},
  fieldVisibility,
  onValuesChange,
  onDirtyChange,
}: DynamicFormProps<T>) {
  const { t } = useTranslation('admin');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const shape = React.useMemo(() => {
    const schemaDef = (schema as any)?._def;
    if (!schemaDef) return {};
    if (typeof schemaDef.shape === 'function') {
      return schemaDef.shape();
    }
    return schemaDef.shape || {};
  }, [schema]);

  const getInputType = React.useCallback((field: any, key: string): string => {
    // Helper to get the type from either old Zod v3 (_def.typeName) or new Zod v4 (def.type)
    const getFieldType = (f: any): string | undefined => {
      return f?._def?.typeName || f?.def?.type;
    };

    // Helper to unwrap all wrapper types and find the core type
    const unwrapField = (f: any): any => {
      if (!f) return null;

      const fieldType = getFieldType(f);
      const wrappedTypes = ['ZodOptional', 'ZodNullable', 'ZodEffects', 'ZodPreprocess', 'optional', 'nullable', 'effects', 'preprocess'];

      if (fieldType && wrappedTypes.includes(fieldType)) {
        // Try various paths to get the inner type
        const innerType = f._def?.innerType || f._def?.schema || f._def?.type ||
                         f.def?.innerType || f.def?.schema || f.def?.type;

        if (innerType) {
          return unwrapField(innerType);
        }
      }

      return f;
    };

    // Unwrap to get the core field type
    const coreField = unwrapField(field);
    const coreFieldType = getFieldType(coreField);

    // Media upload detection based on field name
    if (/^(image|audio|video|document)Url$/i.test(key)) return 'media-upload';

    const loweredKey = typeof key === 'string' ? key.toLowerCase() : '';

    if (loweredKey.includes('datetime')) return 'datetime';

    if (coreFieldType === 'ZodNumber' || coreFieldType === 'number') return 'number';
    if (coreFieldType === 'ZodBoolean' || coreFieldType === 'boolean') return 'checkbox';
    if (coreFieldType === 'ZodDate' || coreFieldType === 'date') return 'date';
    if (coreFieldType === 'ZodArray' || coreFieldType === 'array') return 'relation-many';

    // Check for large text fields
    if (coreField?.maxLength && coreField.maxLength > 1000) {
      return 'textarea';
    }

    // Check if field has date-related description/metadata
    const description = coreField?._def?.description || coreField?.def?.description || '';
    if (description.toLowerCase().includes('date') || description.toLowerCase().includes('birth')) {
      return 'date';
    }

    // Fallback: treat fields that look like dates as date inputs even if the
    // schema comes through as a string (e.g. drizzle date columns using
    // `mode: "string"`).
    if (typeof key === 'string' && (loweredKey.endsWith('date') || loweredKey.includes('date'))) {
      return 'date';
    }

    return 'text';
  }, []);

  const normalizeDateValue = (value: unknown) => {
    if (!value) return undefined;
    if (value instanceof Date) return value;
    if (typeof value === 'number') {
      const dateFromNumber = new Date(value);
      return Number.isNaN(dateFromNumber.getTime()) ? undefined : dateFromNumber;
    }
    if (typeof value === 'string') {
      if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
        const [year, month, day] = value.split('-').map(Number);
        if (
          Number.isFinite(year) &&
          Number.isFinite(month) &&
          Number.isFinite(day)
        ) {
          const localDate = new Date(year!, (month! - 1), day!);
          return Number.isNaN(localDate.getTime()) ? undefined : localDate;
        }
        return undefined;
      }

      const parsed = new Date(value);
      if (Number.isNaN(parsed.getTime())) {
        return undefined;
      }

      if (
        parsed.getUTCHours() === 0 &&
        parsed.getUTCMinutes() === 0 &&
        parsed.getUTCSeconds() === 0 &&
        parsed.getUTCMilliseconds() === 0
      ) {
        return new Date(parsed.getUTCFullYear(), parsed.getUTCMonth(), parsed.getUTCDate());
      }

      return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
    }

    return undefined;
  };

const formatFieldLabel = (key: string, override?: string) =>
  override ??
  key
    .charAt(0)
    .toUpperCase()
    .concat(key.slice(1))
    .replace(/([A-Z])/g, ' $1')
    .replace(/Url$/i, ' File');

const SELECT_CLEAR_VALUE = '__dynamic_form_clear__';

  const normalizedDefaultValues = React.useMemo(() => {
    if (!defaultValues || Object.keys(defaultValues).length === 0) {
      return defaultValues;
    }

    const entries = Object.entries(defaultValues).reduce<Record<string, unknown>>((acc, [key, value]) => {
      const field = (shape as any)[key];
      if (field) {
        const inputType = getInputType(field, key);
        if (inputType === 'date') {
          acc[key] = normalizeDateValue(value);
          return acc;
        }
      }

      acc[key] = value;
      return acc;
    }, {});

    return {
      ...defaultValues,
      ...entries,
    };
  }, [defaultValues, shape, getInputType]);

  const serializeFormData = useFormDateSerializer(shape, getInputType);

  const resolver = React.useMemo<Resolver<z.infer<T>>>(() => {
    const baseResolver = zodResolver(schema as unknown as any) as Resolver<z.infer<T>>;
    return async (values, context, options) => {
      const normalizedValues = serializeFormData(values as Record<string, unknown>);
      const result = await baseResolver(normalizedValues as any, context, options as any);

      return result;
    };
  }, [schema, serializeFormData]);

  const methods = useForm<z.infer<T>>({
    resolver,
    defaultValues: normalizedDefaultValues as any,

  });

  const {
    handleSubmit,
    reset,
    control,
    setValue,
    watch,
    formState: { errors, isDirty },
  } = methods;

  const watchedValues = watch();

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  useEffect(() => {
    reset(normalizedDefaultValues as any);
    onValuesChange?.(normalizedDefaultValues as Partial<z.infer<T>>);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [normalizedDefaultValues, reset]);

  useEffect(() => {
    if (!onValuesChange) return;
    const subscription = watch((values) => onValuesChange(values as Partial<z.infer<T>>));
    return () => subscription.unsubscribe();
  }, [watch, onValuesChange]);
  
  const handleFormSubmit = async (data: z.infer<T>) => {
    setIsSubmitting(true);
    try {
      await onSubmit(data);
      if (!isEditing) {
        reset();
      }
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFormInvalid = (formErrors: FieldErrors<z.infer<T>>) =>
    console.error('DynamicForm validation errors', formErrors);

  return (
    <FormProvider {...methods}>
      <form
        ref={formRef}
        onSubmit={handleSubmit(handleFormSubmit, handleFormInvalid)}
        onKeyDown={(e) => {
          // Prevent form submission on Enter key unless explicitly clicking submit button
          if (e.key === 'Enter' && e.target instanceof HTMLInputElement) {
            e.preventDefault();
          }
        }}
        className="space-y-8"
      >
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          {Object.entries(shape).map(([key, field]: [string, any]) => {
          // Skip id field when creating new records
          if (key === 'id' && !isEditing) return null;
          
          // Skip fields listed in hiddenFields
          if (hiddenFields.includes(key)) return null;

          if (fieldVisibility) {
            const isVisible = fieldVisibility({
              key,
              values: watchedValues as z.infer<T>,
              isEditing,
            });
            if (!isVisible) return null;
          }
          
          // Determine if this is a foreign key field
          const isForeignKey = key.endsWith('Id') && key !== 'imageId' && key !== 'audioId' && key !== 'videoId' && key !== 'documentId'; // Avoid treating media IDs as standard FKs here

          // Proper pluralization for foreign keys
          const getPluralKey = (singularKey: string): string => {
            const base = singularKey.replace(/Id$/, '');
            // Handle special cases
            if (base.endsWith('y') && !['ay', 'ey', 'iy', 'oy', 'uy'].some(v => base.endsWith(v))) {
              return base.slice(0, -1) + 'ies'; // country → countries
            }
            return base + 's'; // level → levels, state → states
          };

          const fieldOptions = isForeignKey ? foreignKeyOptions[getPluralKey(key)] : undefined;
          
          const inputType = getInputType(field, key);

          const fieldLabel = formatFieldLabel(key, labelMappings[key]);
          const fieldId = `dynamic-form-${key}`;
          const lowerKey = key.toLowerCase();
          const shouldSpanFullWidth =
            ['textarea', 'relation-many', 'media-upload'].includes(inputType) ||
            customRenderers[key] ||
            lowerKey.includes('description') ||
            lowerKey.includes('notes');
          const fieldWrapperClasses = cn(
            'flex flex-col gap-2',
            shouldSpanFullWidth ? 'md:col-span-2' : 'md:col-span-1'
          );
          const labelClassName = 'text-sm font-medium text-slate-600';

          // Check if there's a custom renderer for this field
          if (customRenderers[key]) {
            return (
              <div key={key} className={fieldWrapperClasses}>
                <Label htmlFor={fieldId} className={labelClassName}>
                  {fieldLabel}
                </Label>
                <Controller
                  name={key as any}
                  control={control}
                  render={({ field }) => (
                    <>
                      {customRenderers[key]!({
                        field,
                        error: errors[key]?.message as string,
                        methods,
                      })}
                    </>
                  )}
                />
                {errors[key] && (
                  <p className="text-xs font-medium text-destructive">
                    {errors[key]?.message as string}
                  </p>
                )}
              </div>
            );
          }

          return (
            <div key={key} className={fieldWrapperClasses}>
              <Label htmlFor={fieldId} className={labelClassName}>
                {fieldLabel}
              </Label>

              {inputType === 'media-upload' ? (() => {
                const mediaTypeMatch = key.match(/^(image|audio|video|document)Url$/i);
                const mediaType = mediaTypeMatch ? mediaTypeMatch[1].toLowerCase() : 'unknown';
                let acceptedFileTypes: string[] = [];
                let mediaTitle = 'Upload Media';
                let description = 'Select or drag a file';

                switch (mediaType) {
                  case 'image':
                    acceptedFileTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];
                    mediaTitle = 'Upload Image';
                    description = 'Select or drag an image';
                    break;
                  case 'audio':
                    acceptedFileTypes = ['audio/mp3', 'audio/mpeg', 'audio/wav'];
                    mediaTitle = 'Upload Audio';
                    description = 'Select or drag an audio file';
                    break;
                  case 'video':
                    acceptedFileTypes = ['video/mp4', 'video/webm'];
                    mediaTitle = 'Upload Video';
                    description = 'Select or drag a video file';
                    break;
                  case 'document':
                    acceptedFileTypes = [
                      'application/pdf',
                      'application/msword',
                      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
                    ];
                    mediaTitle = 'Upload Document';
                    description = 'Select or drag a document';
                    break;
                }

                const idFieldName = `${mediaType}Id`;

                return (
                  <Controller
                    key={`${key}-controller`}
                    name={key as any}
                    control={control}
                    render={({ field: urlField }) => {
                      const value = urlField.value as string | undefined;
                      return (
                        <div>
                          <MediaUploader
                            compact
                            acceptedFileTypes={acceptedFileTypes}
                            title={mediaTitle}
                            description={description}
                            onUploadComplete={(url, mediaId) => {
                              urlField.onChange(url);
                              if (shape[idFieldName] && mediaId) {
                                setValue(idFieldName as any, mediaId as any);
                              }
                            }}
                          />
                          {value && mediaType === 'image' && (
                            <div className="mt-2">
                              <img
                                src={value}
                                alt="Preview"
                                className="h-24 object-cover rounded-md"
                              />
                            </div>
                          )}
                          {value && mediaType === 'video' && (
                            <div className="mt-2">
                              <video src={value} controls className="h-24 rounded-md" />
                            </div>
                          )}
                          {value && mediaType === 'audio' && (
                            <div className="mt-2">
                              <audio src={value} controls />
                            </div>
                          )}
                          {value && mediaType === 'document' && (
                            <div className="mt-2">
                              <a href={value} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                                View Document
                              </a>
                            </div>
                          )}
                        </div>
                      );
                    }}
                  />
                );
              })() : isForeignKey && fieldOptions ? (
                <Controller
                  name={key as any}
                  control={control}
                  render={({ field }) => {
                    const normalizedValue =
                      field.value === null || field.value === undefined
                        ? undefined
                        : String(field.value);

                    return (
                      <Select
                        value={normalizedValue}
                        onValueChange={(selectedValue) => {
                          if (selectedValue === SELECT_CLEAR_VALUE) {
                            field.onChange(undefined);
                            return;
                          }
                          field.onChange(selectedValue);
                        }}
                        disabled={isSubmitting}
                      >
                        <SelectTrigger id={fieldId} className="w-full text-left">
                          <SelectValue placeholder={`Select ${fieldLabel}`} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={SELECT_CLEAR_VALUE}>{t('shared.form.none')}</SelectItem>
                          {fieldOptions.map((option: any) => {
                            const optionValue = option.id ?? option.value;
                            if (optionValue === undefined || optionValue === null) {
                              return null;
                            }

                            return (
                              <SelectItem key={optionValue} value={String(optionValue)}>
                                {option.label}
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                    );
                  }}
                />
              ) : inputType === "checkbox" ? (
                // Use Switch component for boolean/checkbox fields
                <Controller
                  name={key as any}
                  control={control}
                  render={({ field }) => (
                    <Switch
                      id={fieldId}
                      checked={Boolean(field.value)}
                      onCheckedChange={(checked) => {
                        field.onChange(checked);
                      }}
                    />
                  )}
                />
              ) : inputType === "relation-many" ? (
                <Controller
                  name={key as any}
                  control={control}
                  render={({ field }) => {
                    return (
                      <MultipleSelector
                        value={(field.value as Option[] | undefined) ?? []}
                        onChange={(selected) => field.onChange(selected)}
                        defaultOptions={foreignKeyOptions[key] as Option[]}
                        placeholder={`Select ${fieldLabel}`}
                        badgeVariant="outline"
                        emptyIndicator={
                          <p className="text-center text-lg leading-10 text-gray-600 dark:text-gray-400">
                            no results found.
                          </p>
                        }
                      />
                    );
                  }}
                />
              ) : inputType === "date" ? (
                <Controller
                  name={key as any}
                  control={control}
                  render={({ field }) => {
                    return (
                      <DatePicker
                        value={field.value as Date | undefined}
                        onChange={(date) => {
                          field.onChange(date);
                        }}
                        placeholder={`Select ${labelMappings[key] || key}`}
                        disabled={isSubmitting}
                      />
                    );
                  }}
                />
              ) : inputType === "textarea" ? (
                <Controller
                  name={key as any}
                  control={control}
                  render={({ field }) => (
                    <TextArea
                      id={fieldId}
                      value={(field.value ?? '') as string}
                      onChange={field.onChange}
                      className="min-h-[120px]"
                      disabled={isSubmitting || (key === 'id' && isEditing)}
                    />
                  )}
                />
              ) : inputType === "datetime" ? (
                // Same shadcn calendar as the "date" branch, plus a time box —
                // never the browser's native datetime-local control.
                <Controller
                  name={key as any}
                  control={control}
                  render={({ field }) => (
                    <DateTimeField
                      value={((field.value ?? '') as string) || undefined}
                      onChange={(next) => field.onChange(next ?? '')}
                      placeholder={`Select ${labelMappings[key] || key}`}
                      disabled={isSubmitting || (key === 'id' && isEditing)}
                    />
                  )}
                />
              ) : (
                <Controller
                  name={key as any}
                  control={control}
                  render={({ field }) => (
                    <Input
                      id={fieldId}
                      type={inputType}
                      value={(field.value ?? '') as string}
                      onChange={field.onChange}
                      disabled={isSubmitting || (key === 'id' && isEditing)}
                    />
                  )}
                />
              )}
              
              {errors[key] && (
                <p className="text-xs font-medium text-destructive">
                  {errors[key]?.message as string}
                </p>
              )}
            </div>
          );
        })}
      </div>
      
        {!hideButtons && (
          <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:justify-end">
            {onClose && (
              <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>
                Cancel
              </Button>
            )}
            <Button type="submit" variant="outline" disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : isEditing ? 'Update' : 'Create'}
            </Button>
          </div>
        )}
      </form>
    </FormProvider>
  );
} 